import { useEffect, useState, useCallback } from "react";
import AmplifiIDImg from "../../images/AmplifiID.png";

//contexts
import { useSampleContext } from "./../../contexts";

import {
  useConnectorDevices,
  useServerDevices,
  useConnectorAppHealth,
} from "../../api";
import styleDate from "../../util/dateStyler";

//components
import { CardD } from "./../ui/Cards";

const WizardDeviceSelection = ({
  step,
  steps,
  isCalibrate,
  changeStep,
  pageID,
}) => {
  const [warning, setWarning] = useState(null);
  const [devices, setDevices] = useState([]);
  const { setCalibrationDevice, setGeometry } = useSampleContext();

  // Memoized handlers to avoid re-creation and re-renders
  const onDevicesError = useCallback((err) => {
    console.error(err);
    setDevices([]);
  }, []);

  const onServerDevicesSuccess = useCallback((serverDevices) => {
    setDevices(
      Array.isArray(serverDevices) && serverDevices.length > 0
        ? [...serverDevices].sort(
            (a, b) => b.lastCalibration - a.lastCalibration
          )
        : []
    );
  }, []);

  // API hooks - run in sequence: health -> connector devices -> server devices
  const connectorAppHealth = useConnectorAppHealth();
  const connectorDevices = useConnectorDevices(
    connectorAppHealth.data,
    null,
    onDevicesError
  );
  const serverDevices = useServerDevices(
    connectorDevices.data?.devices,
    onServerDevicesSuccess,
    onDevicesError
  );

  // Unified refresh logic for both manual refresh and step change
  const { refetch: refetchHealth } = connectorAppHealth;
  const { refetch: refetchConnectorDevices } = connectorDevices;
  const { refetch: refetchServerDevices } = serverDevices;

  const refreshDevices = useCallback(async () => {
    setDevices([]);
    try {
      const healthResult = await refetchHealth();
      const health = healthResult?.data;
      const proceed = health?.healthy || health?.healthSupported === false; // legacy supported
      if (proceed) {
        const devicesResult = await refetchConnectorDevices();
        if (
          !devicesResult?.data?.devices ||
          devicesResult.data.devices.length === 0
        ) {
          return;
        }
        try {
          await refetchServerDevices();
        } catch (e2) {
          console.error("Error refetching server devices:", e2);
        }
      }
    } catch (e) {
      console.error("Error during device refresh:", e);
    }
  }, [refetchHealth, refetchConnectorDevices, refetchServerDevices]);

  // Check connector app health on component mount
  useEffect(() => {
    connectorAppHealth.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When health check completes, trigger device scan if healthy
  useEffect(() => {
    if (connectorAppHealth.data?.healthy) {
      connectorDevices.refetch();
    }
    // Show legacy warning if health unsupported (404 case)
    // if (
    //   connectorAppHealth.data &&
    //   connectorAppHealth.data.healthSupported === false
    // ) {
    //   setWarning(
    //     <div
    //       className='alert alert-warning alert-dismissible fade show'
    //       role='alert'
    //     >
    //       <strong>Update Recommended:</strong> New Connector App version
    //       available. Please contact our team for assistance.
    //       <button
    //         type='button'
    //         className='btn-close'
    //         data-bs-dismiss='alert'
    //         aria-label='Close update warning'
    //       ></button>
    //     </div>
    //   );
    // } else {
    //   setWarning(null);
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    connectorAppHealth.data?.healthy,
    connectorAppHealth.data?.healthSupported,
  ]);

  // Remove manual loading state management; rely on derivedLoading below

  // Refresh devices when navigating to this page
  useEffect(() => {
    if (step === pageID && !isCalibrate) {
      refreshDevices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCalibrate, step, pageID]);

  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGeometry({
            type: "Point",
            coordinates: [position.coords.longitude, position.coords.latitude],
          });
        },
        (error) => {
          console.error("Error getting location: ", error);
        }
      );
    }
  }, [setGeometry]);

  // Memoized error message logic
  const getErrorMessage = useCallback(() => {
    // Don't show errors while any relevant query is fetching/loading
    if (connectorAppHealth.isLoading || connectorAppHealth.isFetching)
      return null;
    if (
      !connectorAppHealth.data?.healthy &&
      connectorAppHealth.data?.healthSupported !== false
    ) {
      return {
        title: "Connector App Unavailable",
        message:
          connectorAppHealth.data?.message ||
          "Please ensure the Connector App is running and try again.",
      };
    }
    // Wait until connector devices query has fetched and is not fetching before showing empty state
    if (
      connectorDevices.isLoading ||
      connectorDevices.isFetching ||
      !connectorDevices.isFetched
    )
      return null;
    if ((connectorDevices.data?.devices?.length ?? 0) === 0) {
      return {
        title: "No Devices Found",
        message:
          "Please check all device connections and make sure the device is recognized on the Connector App.",
      };
    }
    // Wait until server devices query has fetched and is not fetching before showing empty state
    if (
      serverDevices.isLoading ||
      serverDevices.isFetching ||
      !serverDevices.isFetched
    )
      return null;
    if (devices.length === 0) {
      return {
        title: "No Devices Found",
        message: "Could not retrieve device information from the server.",
      };
    }
    return null;
  }, [
    connectorAppHealth.isLoading,
    connectorAppHealth.isFetching,
    connectorAppHealth.data,
    connectorDevices.isLoading,
    connectorDevices.isFetching,
    connectorDevices.isFetched,
    connectorDevices.data,
    serverDevices.isLoading,
    serverDevices.isFetching,
    serverDevices.isFetched,
    devices.length,
  ]);

  // Derived loading state
  const derivedLoading =
    connectorAppHealth.isLoading ||
    connectorAppHealth.isFetching ||
    (connectorAppHealth.data?.healthy &&
      (connectorDevices.isLoading || connectorDevices.isFetching)) ||
    (((connectorDevices.data?.devices?.length ?? 0) > 0 ||
      connectorDevices.isFetching) &&
      (serverDevices.isLoading || serverDevices.isFetching));

  if ((step !== pageID && pageID !== "return") || isCalibrate) {
    return null;
  }

  return (
    <CardD
      title='Select a Device'
      subtitle='Click the button that corresponds to your device'
      spacing='col-6'
      buttonTitle='Refresh'
      buttonFunction={refreshDevices}
      buttonStyle='btn-outline-primary'
    >
      {warning}
      <div className='d-flex flex-column flex-grow-1 gap-3 justify-content-center'>
        {derivedLoading && (
          <div className='mx-auto'>
            <div className='spinner-border' role='status'>
              <span className='sr-only'>Loading...</span>
            </div>
          </div>
        )}
        {!derivedLoading &&
          devices.length > 0 &&
          devices.map((d) => (
            <div className='shadow card' key={d.deviceId}>
              <div className='card-body'>
                <div className='d-flex flex-row align-items-center justify-content-center flex-wrap'>
                  <div className='d-flex justify-content-center'>
                    <img
                      src={AmplifiIDImg}
                      alt='AmplifiID Device'
                      width={207}
                      height={140}
                    />
                  </div>
                  <div className='flex-grow-1'>
                    <h5 className='card-title w-75'>{d.name}</h5>
                    <h6 className='card-subtitle mb-4 text-muted'>
                      Last calibrated:{" "}
                      {d.lastCalibration
                        ? styleDate(d.lastCalibration)
                        : "Never"}
                    </h6>
                  </div>
                  {d.lastCalibration >= new Date(Date.now() - 3600000 * 24) ? (
                    <div className='d-flex flex-column justify-content-center flex-grow-1'>
                      <button
                        type='button'
                        className='btn btn-success mx-auto w-75'
                        aria-label={`Select device ${d.name}`}
                        onClick={() => {
                          setCalibrationDevice(d);
                          setDevices([]);
                          changeStep("forward");
                        }}
                      >
                        Select
                      </button>
                      <button
                        type='button'
                        className='btn btn-outline-warning mx-auto w-75 mt-2'
                        aria-label={`Calibrate device ${d.name}`}
                        onClick={() => {
                          setCalibrationDevice(d);
                          setDevices([]);
                          changeStep("calibrate");
                        }}
                      >
                        Calibrate
                      </button>
                    </div>
                  ) : (
                    <div className='col-4 d-flex flex-column justify-content-center'>
                      <button
                        type='button'
                        className='btn btn-warning mx-auto w-75'
                        aria-label={`Calibrate device ${d.name}`}
                        onClick={() => {
                          setCalibrationDevice(d);
                          setDevices([]);
                          changeStep("calibrate");
                        }}
                      >
                        Calibrate
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        {!derivedLoading &&
          devices.length === 0 &&
          (() => {
            const errorMsg = getErrorMessage();
            if (!errorMsg) return null;
            return (
              <div className='text-center'>
                <i className='bi bi-x-octagon-fill fs-1 text-danger'></i>
                <h5>{errorMsg.title}</h5>
                <h6>{errorMsg.message}</h6>
              </div>
            );
          })()}
      </div>
    </CardD>
  );
};

export default WizardDeviceSelection;
